import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import SummaryCards from "@/components/dashboard/summary-cards";
import SpendAnalysisChart from "@/components/dashboard/spend-analysis-chart";
import TopApplications from "@/components/dashboard/top-applications";
import OptimizationRecommendations from "@/components/dashboard/optimization-recommendations";
import RecentActivity from "@/components/dashboard/recent-activity";
import UpcomingRenewalsTable from "@/components/dashboard/upcoming-renewals-table-simple";
import SimpleAddLicenseDialog from "@/components/licenses/simple-add-license-dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { PlusCircle, Gauge, ArrowUpRight, CheckCircle } from "lucide-react";
import { PaymentCardOverview } from "@/components/dashboard/payment-card-overview";
import { RenewalForecast } from "@/components/dashboard/renewal-forecast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useLocation } from "wouter";
import { LimitAlertsBanner } from "@/components/alerts/limit-alerts-banner";
import { MaintenanceBanner } from "@/components/ui/maintenance-banner";

export default function Dashboard() {
  const { toast } = useToast();
  const { user, isSuperAdmin } = useAuth();
  const [addLicenseOpen, setAddLicenseOpen] = useState(false);
  const [location] = useLocation();
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  
  // Check if user is admin (includes both super admin and regular admin)
  const isAdmin = user?.role === 'admin';
  
  // Check for subscription success query parameters
  useEffect(() => {
    const url = new URL(window.location.href);
    const subscriptionStatus = url.searchParams.get('subscription');
    const plan = url.searchParams.get('plan');
    
    if (subscriptionStatus === 'success') {
      setShowSubscriptionSuccess(true);
      setSubscriptionPlan(plan || 'new');
      
      // Optional: Remove the query parameters after processing
      // This prevents the success message from showing on page refresh
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      
      // Auto-hide the success message after 10 seconds
      const timer = setTimeout(() => {
        setShowSubscriptionSuccess(false);
      }, 10000);
      
      return () => clearTimeout(timer);
    }
  }, [location]);
  
  return (
    <div className="space-y-6 animate-in">
      {/* System Maintenance Banner */}
      <MaintenanceBanner />
      
      {/* Plan Limit and Subscription Alerts */}
      <LimitAlertsBanner />
      
      {/* Subscription Success Alert */}
      {showSubscriptionSuccess && (
        <Alert className="border border-emerald-200/60 bg-gradient-to-r from-emerald-50/90 to-green-50/90 backdrop-blur-sm shadow-lg rounded-xl animate-slide-up modern-card">
          <CheckCircle className="h-5 w-5 !text-emerald-600" />
          <AlertTitle className="text-emerald-800 font-semibold">Subscription Activated</AlertTitle>
          <AlertDescription className="text-emerald-700">
            {subscriptionPlan === 'free' 
              ? 'Your free plan has been activated successfully. Welcome to the platform!'
              : `Your ${subscriptionPlan} subscription has been activated successfully. Your account is now ready to use with all features enabled.`}
          </AlertDescription>
        </Alert>
      )}
      
      {/* Super Admin Alert */}
      {isSuperAdmin && (
        <Alert className="border border-amber-200/60 bg-gradient-to-r from-amber-50/90 to-orange-50/90 backdrop-blur-sm shadow-lg rounded-xl animate-slide-up modern-card">
          <Gauge className="h-5 w-5 !text-amber-600" />
          <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-amber-800 font-medium">You have super admin access to the platform.</span>
            <Button 
              size="sm" 
              variant="outline" 
              className="border-amber-300 hover:border-amber-400 bg-gradient-to-r from-amber-100 to-orange-100 hover:from-amber-200 hover:to-orange-200 text-amber-700 shadow-md transition-all duration-200 button-modern w-full sm:w-auto"
              onClick={() => window.location.href = "/admin"}
            >
              <span>Go to Admin Dashboard</span>
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Dashboard Header */}
      <div className="modern-card rounded-2xl p-6 sm:p-8 animate-fade-in">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-2">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Dashboard Overview
            </h2>
            <p className="text-slate-600 text-sm sm:text-base lg:text-lg">
              Monitor your SaaS portfolio at a glance
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              size="default" 
              onClick={() => setAddLicenseOpen(true)}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 button-modern rounded-xl px-6 py-3"
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              Add License
            </Button>
            
            {/* Add License Dialog */}
            <SimpleAddLicenseDialog 
              open={addLicenseOpen} 
              onOpenChange={setAddLicenseOpen} 
            />
          </div>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="animate-fade-in">
        <SummaryCards />
      </div>
      
      {/* Upcoming Renewals Table - Full width, high priority positioning */}
      <div className="animate-slide-up animate-delay-100">
        <UpcomingRenewalsTable />
      </div>
      
      {/* Admin-only Payment Methods and Cost Forecast */}
      {isAdmin && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-responsive animate-slide-up animate-delay-200">
          <div className="xl:col-span-2">
            <PaymentCardOverview />
          </div>
          <div className="xl:col-span-1">
            <RenewalForecast />
          </div>
        </div>
      )}
      
      {/* Cost Optimization and Monthly Spend Analysis */}
      <div className="grid-responsive-1-2 gap-responsive animate-slide-up animate-delay-300">
        <div className="lg:col-span-1">
          <OptimizationRecommendations />
        </div>
        {isAdmin && (
          <div className="lg:col-span-1">
            <SpendAnalysisChart />
          </div>
        )}
      </div>
      
      {/* Top Applications by Usage - Full Width */}
      <div className="animate-slide-up animate-delay-400">
        <TopApplications />
      </div>
      
      {/* Admin-only Recent Activity */}
      {isAdmin && (
        <div className="animate-slide-up animate-delay-500">
          <RecentActivity />
        </div>
      )}
    </div>
  );
}
